import { useCallback, useMemo, useState } from "react";

import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { SortingState } from "@tanstack/react-table";

import { Connection } from "src/api/connectApi/types";
import { TableComp as Table } from "src/base-components/Table";
import {
  ProviderSdkBundleResponse,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import {
  ConnectionTypeLabel,
  filterRowsByType,
  columns as tableColumns,
} from "src/connections/columns";
import { ConnectionListRow } from "src/connections/sdk/rows";
import { EmptyState } from "src/design-system/EmptyState";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";

const DEFAULT_SORTING: SortingState = [{ id: "connections", desc: false }];

const getRowDataLoc = (row: ConnectionListRow): string => {
  switch (row.kind) {
    case "connection":
      return `connection-row-${row.connection.name}-${row.connection.id}}`;
    case "sdkScript":
      return `sdk-script-row-${row.id}`;
    case "sdkBundle":
      return `sdk-bundle-row-${row.bundle.id}`;
  }
};

type Props = {
  rows: ConnectionListRow[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  testingConnection?: Connection;
  searchQuery?: string;
  onEditSdkBundle: (bundle: ProviderSdkBundleResponse) => void;
  onDeleteSdkBundle: (bundle: ProviderSdkBundleResponse) => void;
  onAddScriptToBundle: (script: ProviderSdkConfigResponse) => void;
  onEditScript: (script: ProviderSdkConfigResponse) => void;
  onDeleteScript: (script: ProviderSdkConfigResponse) => void;
};

export const ConnectionList: React.FC<Props> = ({
  onEdit,
  onDelete,
  onTest,
  testingConnection,
  rows,
  searchQuery,
  onEditSdkBundle,
  onDeleteSdkBundle,
  onAddScriptToBundle,
  onEditScript,
  onDeleteScript,
}) => {
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [typeFilter, setTypeFilter] = useState<ConnectionTypeLabel[]>([]);

  const showManifestVersion = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.showManifestVersion,
  );

  const typeFilteredRows = useMemo(
    () => filterRowsByType(rows, typeFilter),
    [rows, typeFilter],
  );

  const showTypeFilterEmptyState =
    rows.length > 0 && typeFilter.length > 0 && typeFilteredRows.length === 0;

  const clearTypeFilter = useCallback(() => {
    setTypeFilter([]);
  }, []);

  const columns = useMemo(
    () =>
      tableColumns({
        onDelete,
        onEdit,
        onTest,
        testingConnection,
        onEditSdkBundle,
        onDeleteSdkBundle,
        onAddScriptToBundle,
        onEditScript,
        onDeleteScript,
        typeFilter,
        onTypeFilterChange: setTypeFilter,
        showManifestVersion,
      }),
    [
      onDelete,
      onEdit,
      onTest,
      testingConnection,
      onEditSdkBundle,
      onDeleteSdkBundle,
      onAddScriptToBundle,
      onEditScript,
      onDeleteScript,
      typeFilter,
      showManifestVersion,
    ],
  );

  const onRowClick = (row: ConnectionListRow) => {
    switch (row.kind) {
      case "connection":
        return onEdit(row.connection.id);
      case "sdkBundle":
        return onEditSdkBundle(row.bundle);
      case "sdkScript":
        return onEditScript(row.script);
    }
  };

  return (
    <div className="relative h-full min-h-0">
      <Table
        columns={columns}
        data={typeFilteredRows}
        getRowId={(row) => row.id}
        sorting={sorting}
        onSortingChange={setSorting}
        globalFilter={searchQuery}
        rowClassName="cursor-pointer"
        rowPropsGetter={(rowData) => ({
          "data-loc": getRowDataLoc(rowData.original),
          onClick: () => onRowClick(rowData.original),
        })}
        clientSideFiltering
        disableVirtualization
      />
      {showTypeFilterEmptyState && (
        <div
          className="pointer-events-none absolute inset-x-0 top-12 bottom-0 flex items-center justify-center"
          data-loc="connections-filter-empty-state"
        >
          <div className="pointer-events-auto">
            <EmptyState
              actionButton={{
                dataLoc: "connections-clear-filters",
                label: "Clear filter",
                onClick: clearTypeFilter,
              }}
              description="Try adjusting your filter to find what you're looking for."
              headline="No connections found"
              icon={faFilter}
              size="compact"
            />
          </div>
        </div>
      )}
    </div>
  );
};
